import React, { useRef, useState, useCallback, useEffect } from 'react';
import { ChevronRight, ChevronLeft, Check, MapPin, Users, Mail, Calendar } from 'lucide-react';
import DatePickerCalendar from '../components/DatePickerCalendar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { icon: Calendar, label: 'Your Date' },
  { icon: MapPin, label: 'Your Venue' },
  { icon: Users, label: 'Your Names' },
  { icon: Mail, label: 'Your Email' },
];

const GOLD = '#C8A967';
const GOLD_LIGHT = '#E8D9B0';

const WeddingFair = () => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dateResult, setDateResult] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  // Form data
  const [selectedDate, setSelectedDate] = useState('');
  const [venue, setVenue] = useState('');
  const [name1, setName1] = useState('');
  const [name2, setName2] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const resetTimerRef = useRef(null);

  // Cleanup pending reset timer on unmount to avoid setState-after-unmount warnings
  useEffect(() => () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
  }, []);

  // Scroll to top on every step change so the header + step title are always visible
  useEffect(() => {
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { window.scrollTo(0, 0); }
  }, [step, submitted]);

  // iPad/mobile keyboard fix — pull the focused input into the middle of the viewport
  // so the on-screen keyboard doesn't cover it.
  const scrollIntoView = useCallback((e) => {
    const el = e.target;
    setTimeout(() => {
      try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch {}
    }, 300);
  }, []);

  const resetForm = () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    setStep(0);
    setSelectedDate('');
    setVenue('');
    setName1('');
    setName2('');
    setEmail('');
    setPhone('');
    setDateResult(null);
    setSubmitted(false);
    setSubmitMessage('');
    setLoading(false);
  };

  const checkDate = async () => {
    if (!selectedDate) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/check-date`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate }),
      });
      const data = await res.json();
      setDateResult(data);
    } catch {
      setDateResult({ status: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!email || !name1 || !name2 || !venue || !phone) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/wedding-fair-submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name1, name2, venue, wedding_date: selectedDate, phone }),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        setSubmitMessage(data.message);
        resetTimerRef.current = setTimeout(resetForm, 6000);
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch {
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    available: 'bg-emerald-500/20 border-emerald-400 text-emerald-300',
    limited: 'bg-amber-500/20 border-amber-400 text-amber-300',
    unavailable: 'bg-red-500/20 border-red-400 text-red-300',
    error: 'bg-red-500/20 border-red-400 text-red-300',
  };

  // Success state
  if (submitted) {
    return (
      <div data-testid="wedding-fair-page" className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: '#1A1A1A' }}>
        <div className="text-center max-w-lg animate-fade-up">
          <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-8">
            <Check className="w-10 h-10 text-emerald-400" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-white mb-4">You're all set!</h2>
          <p className="text-white/70 text-base md:text-lg mb-2">{submitMessage}</p>
          <p className="text-white/40 text-sm mb-10">Check your inbox for your personalised films.</p>
          <button
            data-testid="wf-submit-another"
            onClick={resetForm}
            className="inline-flex items-center gap-2 text-black font-semibold px-8 py-4 rounded-full text-sm uppercase tracking-widest transition-transform hover:scale-[1.02]"
            style={{ background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_LIGHT} 50%, ${GOLD} 100%)` }}
          >
            Submit another <ChevronRight className="w-4 h-4" />
          </button>
          <p className="text-white/60 text-sm mt-6">Form will auto-reset in a few seconds</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="wedding-fair-page" className="min-h-screen flex flex-col" style={{ backgroundColor: '#1A1A1A' }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-14 pb-10 max-w-4xl w-full flex-1">
        {/* HERO HEADING — visible on step 0 only, hidden once user commits */}
        {step === 0 && (
          <h1
            data-testid="wf-hero-heading"
            className="font-serif text-center leading-[1.05] mb-10 md:mb-14"
            style={{
              color: GOLD_LIGHT,
              fontSize: 'clamp(2rem, 6vw, 4.5rem)',
              letterSpacing: '0.01em',
            }}
          >
            Win Photography for FREE
          </h1>
        )}

        {/* PROGRESS */}
        <div className="max-w-2xl mx-auto mb-8 md:mb-10">
          <div className="grid grid-cols-4 gap-2">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === step;
              const isDone = i < step;
              return (
                <div key={i} className="flex flex-col items-center gap-2">
                  <div
                    className={`w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isDone ? 'text-black' : isActive ? 'text-black' : 'bg-white/[0.06] text-white/40'
                    }`}
                    style={
                      isDone || isActive
                        ? { background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)` }
                        : undefined
                    }
                  >
                    {isDone ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span
                    className={`text-[11px] md:text-xs transition-colors ${
                      isActive ? 'font-medium' : isDone ? 'text-white/60' : 'text-white/40'
                    }`}
                    style={isActive ? { color: GOLD_LIGHT } : undefined}
                  >
                    {s.label}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-4 gap-2 mt-3">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className="h-0.5 rounded-full transition-all duration-500"
                style={{ background: i <= step ? GOLD : 'rgba(255,255,255,0.08)' }}
              />
            ))}
          </div>
        </div>

        {/* STEP HEADER */}
        <div className="text-center mb-5 md:mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] mb-3" style={{ color: GOLD_LIGHT }}>
            {step === 3 ? 'Final step' : `Step ${step + 1} of 4`}
          </p>
          <h2 className="font-serif text-2xl md:text-3xl text-white mb-2">
            {step === 0 && 'When is the big day?'}
            {step === 1 && 'Where are you getting married?'}
            {step === 2 && "What are your names?"}
            {step === 3 && "Where should we send your films?"}
          </h2>
          <p className="text-white/50 text-sm md:text-base">
            {step === 0 && "Enter your details and we'll send you our work at your venue."}
            {step === 1 && "We'll find our films from your venue."}
            {step === 2 && "So we can personalise your email."}
            {step === 3 && "We'll send your personalised venue films straight away."}
          </p>
        </div>

        {/* STEP 0 — DATE */}
        {step === 0 && (
          <div className="animate-fade-up max-w-2xl mx-auto">
            <div className="bg-white/[0.04] backdrop-blur-lg rounded-2xl p-3 md:p-4 border border-white/10">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <DatePickerCalendar
                  value={selectedDate}
                  onChange={(d) => { setSelectedDate(d); setDateResult(null); }}
                  className="flex-1"
                />
                <button
                  data-testid="wf-date-btn"
                  onClick={checkDate}
                  disabled={!selectedDate || loading}
                  className="text-black font-semibold px-6 md:px-8 py-3.5 rounded-xl transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
                  style={{ background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)` }}
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  ) : (
                    <>Check <ChevronRight className="w-4 h-4" /></>
                  )}
                </button>
              </div>
            </div>
            {dateResult && (
              <div className={`mt-4 p-4 rounded-xl border ${statusColors[dateResult.status]} animate-fade-up`}>
                <p className="font-semibold text-sm">{dateResult.message}</p>
                {dateResult.urgency && <p className="text-xs mt-1 opacity-80">{dateResult.urgency}</p>}
                {dateResult.status === 'unavailable' && (
                  <p className="text-xs mt-2 opacity-70">Try a different date above.</p>
                )}
              </div>
            )}
            {dateResult && dateResult.status !== 'unavailable' && (
              <button
                data-testid="wf-date-next-btn"
                onClick={() => setStep(1)}
                className="w-full mt-4 text-black font-semibold py-3.5 rounded-xl transition-all duration-300 hover:scale-[1.01] flex items-center justify-center gap-2"
                style={{ background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)` }}
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        {/* STEP 1 — VENUE */}
        {step === 1 && (
          <div className="animate-fade-up max-w-2xl mx-auto">
            <div className="bg-white/[0.04] backdrop-blur-lg rounded-2xl p-3 md:p-4 border border-white/10">
              <input
                data-testid="wf-venue-input"
                type="text"
                value={venue}
                onChange={(e) => setVenue(e.target.value)}
                onFocus={scrollIntoView}
                placeholder="e.g. Gaynes Park"
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-white/40 focus:outline-none focus:border-white/30 transition-all"
                autoFocus
              />
            </div>
            <button
              data-testid="wf-venue-btn"
              onClick={() => venue.trim() && setStep(2)}
              disabled={!venue.trim()}
              className="w-full mt-4 text-black font-semibold py-3.5 rounded-xl transition-all duration-300 hover:scale-[1.01] disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)` }}
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              data-testid="wf-back-btn-1"
              onClick={() => setStep(0)}
              className="w-full mt-2 text-white/50 hover:text-white text-sm font-medium py-2 flex items-center justify-center gap-1.5 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
          </div>
        )}

        {/* STEP 2 — NAMES */}
        {step === 2 && (
          <div className="animate-fade-up max-w-2xl mx-auto">
            <div className="space-y-3">
              <div className="bg-white/[0.04] backdrop-blur-lg rounded-2xl p-3 md:p-4 border border-white/10">
                <input
                  data-testid="wf-name1-input"
                  type="text"
                  value={name1}
                  onChange={(e) => setName1(e.target.value)}
                  onFocus={scrollIntoView}
                  placeholder="Your first name"
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-white/40 focus:outline-none focus:border-white/30 transition-all"
                  autoFocus
                />
              </div>
              <div className="bg-white/[0.04] backdrop-blur-lg rounded-2xl p-3 md:p-4 border border-white/10">
                <input
                  data-testid="wf-name2-input"
                  type="text"
                  value={name2}
                  onChange={(e) => setName2(e.target.value)}
                  onFocus={scrollIntoView}
                  placeholder="Your partner's first name"
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-white/40 focus:outline-none focus:border-white/30 transition-all"
                />
              </div>
            </div>
            <button
              data-testid="wf-names-btn"
              onClick={() => name1.trim() && name2.trim() && setStep(3)}
              disabled={!name1.trim() || !name2.trim()}
              className="w-full mt-4 text-black font-semibold py-3.5 rounded-xl transition-all duration-300 hover:scale-[1.01] disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)` }}
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              data-testid="wf-back-btn-2"
              onClick={() => setStep(1)}
              className="w-full mt-2 text-white/50 hover:text-white text-sm font-medium py-2 flex items-center justify-center gap-1.5 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
          </div>
        )}

        {/* STEP 3 — EMAIL + SUBMIT */}
        {step === 3 && (
          <div className="animate-fade-up max-w-2xl mx-auto">
            <div className="bg-white/[0.04] backdrop-blur-lg rounded-2xl p-3 md:p-4 border border-white/10 mb-3">
              <input
                data-testid="wf-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={scrollIntoView}
                placeholder="your@email.com"
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-white/40 focus:outline-none focus:border-white/30 transition-all"
                autoFocus
              />
            </div>
            <div className="bg-white/[0.04] backdrop-blur-lg rounded-2xl p-3 md:p-4 border border-white/10 mb-3">
              <input
                data-testid="wf-phone-input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onFocus={scrollIntoView}
                inputMode="tel"
                autoComplete="tel"
                placeholder="Mobile number"
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-white/40 focus:outline-none focus:border-white/30 transition-all"
              />
            </div>

            {/* Summary */}
            <div className="bg-white/[0.03] rounded-xl p-4 mb-4 border border-white/10">
              <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Your details</p>
              <div className="space-y-1.5 text-sm">
                <p className="text-white/80"><span className="text-white/40 w-16 inline-block">Date:</span> {selectedDate}</p>
                <p className="text-white/80"><span className="text-white/40 w-16 inline-block">Venue:</span> {venue}</p>
                <p className="text-white/80"><span className="text-white/40 w-16 inline-block">Couple:</span> {name1} & {name2}</p>
              </div>
            </div>

            <button
              data-testid="wf-submit-btn"
              onClick={handleSubmit}
              disabled={!email.trim() || !phone.trim() || loading}
              className="w-full text-black font-bold py-4 rounded-xl transition-all duration-300 hover:scale-[1.01] disabled:opacity-50 flex items-center justify-center gap-2 text-sm uppercase tracking-widest"
              style={{ background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)` }}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Sending your films...
                </span>
              ) : (
                'Send Me My Films'
              )}
            </button>
            <button
              type="button"
              data-testid="wf-back-btn-3"
              onClick={() => setStep(2)}
              disabled={loading}
              className="w-full mt-2 text-white/50 hover:text-white text-sm font-medium py-2 flex items-center justify-center gap-1.5 transition-colors disabled:opacity-40"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>

            {submitMessage && !submitted && (
              <p className="text-red-400 text-xs text-center mt-3">{submitMessage}</p>
            )}
            <p className="text-white/30 text-xs text-center mt-3">We'll never share your information.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeddingFair;
